import { useLayoutEffect, useRef, useState } from 'react';
import { BiVolumeFull, BiVolumeMute } from 'react-icons/bi';
import { caixasSeTocam, posicionarBalao, posicionarSeta, TAMANHO_DA_SETA } from './posicao';
import { Z_TUTORIAL } from './TutorialContext';
import { useFala } from './useFala';
import { textoFalado } from './voz';

const TAMANHO_ANTES_DE_MEDIR = { width: 340, height: 190 };

const Balao = ({
  passo,
  indice,
  total,
  rect,
  status,
  animar = false,
  jaFeito = false,
  vozLigada = false,
  aoAlternarVoz,
  aoVoltar,
  aoAvancar,
  aoPular,
  aoMudarSeta,
}) => {
  const ref = useRef(null);
  const [caixa, setCaixa] = useState(TAMANHO_ANTES_DE_MEDIR);

  useFala(textoFalado(passo), vozLigada);

  // Mede depois de montar: a altura depende do texto, e um tamanho chutado
  // poria o balão em cima do alvo nos textos longos.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    setCaixa((anterior) =>
      anterior.width === width && anterior.height === height ? anterior : { width, height }
    );
  }, [passo, rect]);

  const janela = { width: window.innerWidth, height: window.innerHeight };
  const calculada = rect
    ? posicionarBalao(rect, caixa, janela, passo.lado)
    : {
        lado: passo.lado ?? 'bottom',
        top: janela.height / 2 - caixa.height / 2,
        left: janela.width / 2 - caixa.width / 2,
      };

  // Enquanto o alvo novo não assenta, o recorte segura o anterior e o balão
  // segura a posição junto; recalcular em volta do alvo velho fazia o balão
  // andar duas vezes na troca de passo.
  const anterior = useRef(null);
  const segurar = status === 'procurando' && rect !== null && anterior.current !== null;
  const posicao = segurar ? anterior.current : calculada;
  useLayoutEffect(() => {
    if (!segurar) anterior.current = posicao;
  });

  // Sem lado que caiba, o balão é trazido para dentro da janela e pode parar
  // em cima da seta, que cobriria os botões dele: aí a seta some.
  const setaOculta = (() => {
    if (!rect) return true;
    const seta = posicionarSeta(rect, posicao.lado);
    return caixasSeTocam(
      { ...seta, width: TAMANHO_DA_SETA, height: TAMANHO_DA_SETA },
      { top: posicao.top, left: posicao.left, width: caixa.width, height: caixa.height }
    );
  })();

  const aoMudarSetaRef = useRef(aoMudarSeta);
  aoMudarSetaRef.current = aoMudarSeta;
  useLayoutEffect(() => {
    aoMudarSetaRef.current({ lado: posicao.lado, oculta: setaOculta });
  }, [posicao.lado, setaOculta]);

  const ultimo = indice === total - 1;
  const ausente = status === 'ausente';
  const mostraProximo = !passo.exigeClique || ausente || jaFeito;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-balao-titulo"
      className={`fixed w-[min(340px,calc(100vw-24px))] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 shadow-vox-2xl ${
        animar ? 'tutorial-desliza' : ''
      }`}
      style={{ top: posicao.top, left: posicao.left, zIndex: Z_TUTORIAL }}
    >
      {/* `key` no passo: o texto novo entra animado enquanto a caixa desliza. */}
      <div key={indice} className="tutorial-passo-entra">
        <div className="mb-1 flex items-center justify-between gap-3">
          <h2 id="tutorial-balao-titulo" className="text-sm font-semibold text-gray-900 dark:text-white">
            {passo.titulo}
          </h2>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {indice + 1}/{total}
            </span>
            <button
              type="button"
              onClick={aoAlternarVoz}
              aria-label={vozLigada ? 'Silenciar a narração' : 'Ligar a narração'}
              title={vozLigada ? 'Silenciar a narração' : 'Ligar a narração'}
              className="rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            >
              {vozLigada ? <BiVolumeFull className="h-4 w-4" /> : <BiVolumeMute className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{passo.texto}</p>

        {ausente && (
          <p className="mt-3 rounded-md bg-yellow-50 px-2 py-1.5 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
            Não encontrei este item na tela. Ele pode não estar disponível para o seu nível de acesso.
          </p>
        )}

        {passo.exigeClique && !ausente && !jaFeito && (
          <p className="mt-3 text-xs font-medium text-vox-green-700 dark:text-vox-green-400">
            Clique no item destacado para continuar.
          </p>
        )}

        {passo.avancaAoDigitar && !ausente && (
          <p className="mt-3 text-xs font-medium text-vox-green-700 dark:text-vox-green-400">
            Digite no campo destacado. O tutorial segue quando você parar de digitar.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={aoPular}
            className="rounded-md px-2 py-1.5 text-xs text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            Pular tutorial
          </button>

          <div className="flex items-center gap-2">
            {indice > 0 && (
              <button
                type="button"
                onClick={aoVoltar}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Voltar
              </button>
            )}
            {/* Passo que exige o clique não ganha Próximo: senão o tutorial vira
                slideshow e a pessoa termina sem ter feito a ação. Ele volta
                quando o alvo é dado como ausente ou quando a pessoa já fez o
                clique e voltou ao passo. */}
            {mostraProximo && (
              <button
                type="button"
                onClick={aoAvancar}
                className="rounded-lg bg-vox-green-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-vox-green-700"
              >
                {ultimo ? 'Concluir' : 'Próximo'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Balao;
